import React, { useEffect, useState } from "react";
import moment from 'moment';
import tobeor from 'tobeor';

import * as DOMPurify from 'dompurify';
import JSON5 from 'json5';
import { cloneDeep } from "lodash";

import { Tab, Tabs } from '@mui/material';

import DonutSmallOutlinedIcon from '@mui/icons-material/DonutSmallOutlined';
import { DonutSmall } from "@mui/icons-material";

import ArrowTooltipCss from "../../../common/ArrowTooltipCss";
import Macros from "../../../common/Macros";
import LoaderNew from "../../../common/LoaderNew";
import TrplSelect from "../../../common/TrplControls/TrplSelect";
import TreeView from "./TreeView";

import { compareJsonStructure } from "../../../utils";

export default React.memo(({
  index,
  classes,
  message,
  llm_models,
  searchValue,
  countTokens,
  onSaveJsonToDB,
  highlightAsSaved,
  modifyJsonKeyVal,
  finetune_classif,
  finetune_template,
  sendClassification,
  highlightAsNotValid,
}) => {
  const [json, setJson] = useState('');
  const [initialJson, setInitialJson] = useState('');
  const [jsonSaved, setJsonSaved] = useState(false);
  const [sendingMacroData, setSendingMacroData] = useState(false);
  const [sendingClassifData, setSendingClassifData] = useState(false);
  const [currentMainTabValue, setCurrentMainTabValue] = useState('fine_text');
  const [currentJsonTabValue, setCurrentJsonTabValue] = useState('human');
  const [countedTokens, setCountedTokens] = useState(null);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [jsonWithKeys, setJsonWithKeys] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (!json) {
      if (message.finetune_compl) {
        setJson(message.finetune_compl);
        setJsonSaved(true);
      } else {
        setJson(template);
      }

      if (!message.fine_text) setCurrentMainTabValue('fine_html');
    }

  }, [highlightAsNotValid, highlightAsSaved, message.finetune_compl, jsonSaved]);

  useEffect(() => {
    if (!message.hasOwnProperty('finetune_compl') && message.parsings && message.parsings.length) {
      const activeParsedModelKey = message.parsings.length - 1;
      setCurrentJsonTabValue(activeParsedModelKey);
    }
    if (message.finetune_compl) setInitialJson(message.finetune_compl);
  }, []);

  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      if (selection.toString() !== '') setSelectedText(selection.toString());
    };

    document.addEventListener('mouseup', handleSelection);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
    };
  }, []);

  const getKeyPositions = (obj, path = '') => {
    const result = {};

    for (const key in obj) {
      const newPath = path === '' ? key : `${path}/${key}`;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        result[key] = getKeyPositions(obj[key], newPath);
      } else if (Array.isArray(obj[key])) {
          result[key] = obj[key].map((item, index) => getKeyPositions(item, `${newPath}/${index}`));
      } else {
        result[key] = newPath;
      }
    }

    return result;
  };

  let currentMessage;
  if (currentMainTabValue === 'full_text') {
    currentMessage = message.full_text;
  } else if (currentMainTabValue === 'fine_text') {
    currentMessage = message.fine_text;
  } else if (currentMainTabValue === 'clas_text') {
    currentMessage = message.clas_text;
  } else if (tobeor(currentMainTabValue, ['full_html', 'fine_html', 'clas_html'])) {

    let htmlMessage;
    if (currentMainTabValue === 'fine_html') htmlMessage = message.fine_html;
    else if (currentMainTabValue === 'full_html') htmlMessage = message.full_html;
    else if (currentMainTabValue === 'clas_html') htmlMessage =  message.clas_html;

    if (htmlMessage) currentMessage = DOMPurify.sanitize(htmlMessage, { USE_PROFILES: { html: true } });

  } else if (tobeor(currentMainTabValue, ['full_html_pre', 'fine_html_pre', 'clas_html_pre'])) {

    if (currentMainTabValue === 'fine_html_pre') currentMessage = message.fine_html;
    else if (currentMainTabValue === 'full_html_pre') currentMessage = message.full_html;
    else if (currentMainTabValue === 'clas_html_pre') currentMessage = message.clas_html;
  }

  const classifColors = [
    'limegreen',
    'red',
    'orange',
    'darkorchid',
    'cornflowerblue',
  ];

  const parseJson5 = (jsonNew, cb) => {
    let parsedJson;
    try {
      if (jsonNew) parsedJson = JSON5.parse(jsonNew);
      else parsedJson = JSON5.parse(template);
    } catch (e) {
      setJsonError(e.toString());
      return console.error(e); // error in the above string (in this case, yes)!
    }
    if (parsedJson) setJsonError('');
    if (cb) cb(parsedJson)
  };

  const setJsonWithKeysToState = (parsedJson) => {
    const jsonWithKeys = parsedJson ? getKeyPositions(parsedJson) : null;
    const deepCloneOfJsonWithKeys = cloneDeep(jsonWithKeys)
    setJsonWithKeys(deepCloneOfJsonWithKeys);
  };

  const prepareMessageTables = (data) => {
    if (!data) return;
    data = data.replaceAll("<table>", "<table style='border: 1px solid'>");
    data = data.replaceAll("<tr>", "<tr style='border: 1px solid'>");
    data = data.replaceAll("</table>", "</table><br>");
    return data;
  };

  const onCountTokensBtnClick = () => {
    setLoadingTokens(true);

    countTokens(message.id, (tokens) => {
      if (tokens) setCountedTokens(tokens);
      setLoadingTokens(false);
    });
  };

  const pasteValToJson = (key) => {
    modifyJsonKeyVal(key, selectedText, json, message.id, (newJson) => {
      setModifiedJson(newJson);
      setSelectedText('');
    });
  };

  const setModifiedJson = (newJson) => {
    setJson(newJson);

    const parsedJson = JSON5.parse(newJson);
    const jsonWithKeys = parsedJson ? getKeyPositions(parsedJson) : null;
    setJsonWithKeys(jsonWithKeys);
  };

  useEffect(() => {
    if (!compareJsonStructure(json, jsonWithKeys)) parseJson5(json, (parsedJson) => setJsonWithKeysToState(parsedJson));
  }, [json])

  const template = finetune_template;

  const llmModelsColors = ['', '#E5D7FF', '#ffd7eb', '#d7deff', '#d7ffe5', '#d7f4ff', '#deffd7', '#fffad7',
  'limegreen',
  'red',
  'orange',
  'darkorchid',
  'cornflowerblue'];

  const currentGptSelectedParsingData = currentJsonTabValue !== 'human' && message.parsings && message.parsings[currentJsonTabValue];
  const gptJson = currentGptSelectedParsingData.result;

  const complicatedTabLabel = (tabVal, tokensVal) => {
    const mainTitle = tabVal && tabVal.includes('_html') ? ``
      : tabVal && tabVal.includes('_text') ? tabVal.slice(0, 4) : tabVal;
    return (
      <React.Fragment>
        {mainTitle ? <span>{mainTitle}<br /></span> : null}
        <div style={{ fontSize: 10, lineHeight: 1 }}>
          {countedTokens && countedTokens[tokensVal] && message[tabVal] ?
            <div className={`${classes.alignCenter} ${classes.nowrap}`}>
              <span style={{ maxWidth: '25px', textOverflow: 'ellipsis' }}>
              {message[tabVal].length}
              </span>&nbsp;/ <DonutSmall style={{ fontSize: 11 }}/> {countedTokens[tokensVal]} 
            </div>
          : countedTokens && countedTokens[tokensVal] ?
            <div className={`${classes.alignCenter} ${classes.nowrap}`}>
              <DonutSmall style={{ fontSize: 11 }}/>
              <span style={{ maxWidth: '35px', textOverflow: 'ellipsis' }}>
                {countedTokens[tokensVal]}
              </span>
            </div>
          : message[tabVal] ? message[tabVal].length : null}
        </div>
      </React.Fragment>
    )
  };

  return (
    <div key={message.id} className={`${classes.resizableX} ${classes.mb20}`}> 
      <span style={{ color: '#4C4C4C', padding: '5px', fontSize: 11, backgroundColor: '#fff' }}>{index}</span>
      <div style={{ border: '1px solid #fff', color: '#4c4c4c', position: 'relative', flex: '60%', minWidth: '300px' }}>
        <div className={`${classes.spaceBetweenStart} ${classes.m5}`}>
          <div className={classes.inlineGrid} style={{ fontSize: 11 }}>
            <span># {message.id} : {moment(message.time).format('DD MMM YYYY, HH:mm')}</span>
            {message.source_time && message.source_time > 0 ? <span>source_time: {moment(message.source_time).format('DD MMM YYYY, HH:mm')}</span> : null}
          </div>
          <div className={classes.alignEndColumn} style={{ position: 'absolute', right: '5px', top: '5px' }}>
            <div className={`${classes.flex} ${classes.mt10}`} style={{ zIndex: 10 }}>
              <div className={`${classes.pointer} ${classes.ml10}`} onClick={() => onCountTokensBtnClick()}>
                <ArrowTooltipCss text={countedTokens ? 'Recount tokens' : 'Count tokens'} placement='bottom' >
                  <div
                    className={`${classes.alignJustifyCenter} ${classes.mr10}`}
                    style={{
                      height: '20px',
                      width: '20px',
                      color: '#4c4c4c',
                      borderRadius: '20px',
                      backgroundColor: '#EEECF8',
                      outline: '1px solid #D9D8ED',
                    }}
                  >
                    {loadingTokens ? <LoaderNew style={{ fontSize: 12 }}/>
                      : <DonutSmallOutlinedIcon style={{ fontSize: 16 }}/>
                    }
                  </div>
                </ArrowTooltipCss>
              </div>
            </div>
          </div>
        </div>

        <div className={`${classes.mt10}`}>
          <div className={`${classes.flex} ${classes.ml10}`}>
            <div className={classes.inlineGrid}>
              {message.amazon_id && <span>amazonId: <strong>{message.amazon_id}</strong></span>}
              <span>to: <strong>{message.to}</strong></span>
              <span>from: <strong>{message.from}</strong></span>
              {message.replyTo && <span>replyTo: <strong>{message.replyTo}</strong></span>}
              {message.subject && <span>subject: <strong>{message.subject}</strong></span>}
            </div>
          </div>

          <div className={`${classes.flex} ${classes.fillAvailable} ${classes.p5}`}>
            <div className={`${classes.fillAvailable} ${classes.mb10}`} style={{ borderBottom: '1px solid #fff', height: '10px' }}></div>
            <div style={{ width: 'fit-content' }}>
              {message.full_text &&
              <div>
                <Tabs
                  selectionFollowsFocus
                  className={`${classes.smallestTabs} ${classes.mr20}`}
                  value={currentMainTabValue}
                  onChange={(e, value) => {
                    setCurrentMainTabValue(value);
                  }}
                >
                  <Tab
                    value={'fine_text'}
                    label={'fine_text'}
                  />
                  <Tab
                    value={'clas_text'}
                    label={'clas_text'}
                  />
                  <Tab
                    value={'full_text'}
                    label={'full_text'}
                  />
                </Tabs>
                <div className={`${classes.alignCenter} ${classes.nowrap}`} style={{ fontSize: 10 }}>
                  <div className={`${classes.alignJustifyCenter} ${classes.nowrap} ${classes.mr5}`} style={{ minWidth: '60px' }}>
                    {message['fine_text'].length}
                    {countedTokens ? (
                      <React.Fragment>
                        &nbsp;/ <DonutSmall style={{ fontSize: 11 }}/> {countedTokens['fine_text_tokens']}
                      </React.Fragment>
                    ) : null}
                  </div>
                  <div className={`${classes.alignJustifyCenter} ${classes.nowrap} ${classes.mr5}`} style={{ minWidth: '60px' }}>
                    {message['clas_text'].length}
                    {countedTokens ? (
                      <React.Fragment>
                        &nbsp;/ <DonutSmall style={{ fontSize: 11 }}/> {countedTokens['clas_text_tokens']}
                      </React.Fragment>
                    ) : null}
                  </div>
                  <div className={`${classes.alignJustifyCenter} ${classes.nowrap} ${classes.mr5}`} style={{ minWidth: '60px' }}>
                    {message['full_text'].length}
                    {countedTokens ? (
                      <React.Fragment>
                        &nbsp;/ <DonutSmall style={{ fontSize: 11 }}/> {countedTokens['full_text_tokens']}
                      </React.Fragment>
                    ) : null}
                  </div>

                </div>
                </div>
              }
            </div>

            <div className={`${classes.mr10}`} style={{ fontSize: 10 }}>
              {message.full_html &&
                <div className={`${classes.alignCenter}`}>
                  <div className={`${classes.alignCenterColumn} ${classes.mr5}`}>
                    <div className={`${classes.alignCenter}`}>
                      <span>FINE&nbsp;</span>
                      <Tabs
                        className={classes.smallestTabs}
                        value={currentMainTabValue}
                        onChange={(e, value) => {
                          setCurrentMainTabValue(value);
                        }}
                      >
                        <Tab
                          value={'fine_html'}
                          label={'html'}
                        />
                        <Tab
                          value={'fine_html_pre'}
                          label={'text'}
                        />
                      </Tabs>
                    </div>
                    <span style={{ marginTop: '1px' }}>{complicatedTabLabel('fine_html', 'fine_html_tokens')}</span>
                  </div>

                  <div className={`${classes.alignCenterColumn} ${classes.mr5}`}>
                    <div className={`${classes.alignCenter}`}>
                      <span>CLAS&nbsp;</span>
                      <Tabs
                        className={classes.smallestTabs}
                        value={currentMainTabValue}
                        onChange={(e, value) => {
                          setCurrentMainTabValue(value);
                        }}
                      >
                        <Tab
                          value={'clas_html'}
                          label={'html'}
                        />
                        <Tab
                          value={'clas_html_pre'}
                          label={'text'}
                        />
                      </Tabs>
                    </div>
                    <span style={{ marginTop: '1px' }}>{complicatedTabLabel('clas_html', 'clas_html_tokens')}</span>
                  </div>

                  <div className={`${classes.alignCenterColumn} ${classes.mr5}`}>
                    <div className={`${classes.alignCenter}`}>
                    <span>FULL&nbsp;</span>
                    <Tabs
                      className={classes.smallestTabs}
                      value={currentMainTabValue}
                      onChange={(e, value) => {
                        setCurrentMainTabValue(value);
                      }}
                    >
                      <Tab
                        value={'full_html'}
                        label={'html'}
                      />
                      <Tab
                        value={'full_html_pre'}
                        label={'text'}
                      />
                    </Tabs>
                    </div>
                    <span style={{ marginTop: '1px' }}>{complicatedTabLabel('full_html', 'full_html_tokens')}</span>
                  </div>
                </div>
              }
            </div>
          </div>

          <div className={`${classes.ml10}`} style={{ overflowX: 'auto' }}>
            {tobeor(currentMainTabValue, ['full_html', 'fine_html', 'clas_html'])
            && /<\/?[a-z][\s\S]*>/i.test(currentMessage) ? (
              <div
                style={{
                  margin: '0px',
                  overflow: 'auto',
                  maxHeight: '70vh',
                  backgroundColor: '#fff',
                }}
                dangerouslySetInnerHTML={{ __html: prepareMessageTables(currentMessage) }}
              />
            ) : (
              <pre style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                {currentMessage}
              </pre>
            )}
          </div>

          <div>
            {message.imgs ? (
              <div className={classes.chatMsgImagesBlock}>
                {message.imgs.map((imgUrl, index) => {
                  return (
                    <a key={index} href={imgUrl} className={classes.chatMsgImageLink} rel="noreferrer" target="_blank">
                      <img src={imgUrl} className={classes.chatMsgImage} alt="mail-img" />
                    </a>
                  );
                })}
              </div>
            ) : null}

            {message.files ? (
              <div className={classes.chatMsgFilesBlock}>
                {message.files.map((fileUrl, index) => {
                  return (
                    <a key={index} href={fileUrl} className={classes.chatMsgFile} rel="noreferrer" target="_blank">
                      {fileUrl}
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className={`resizerX ${classes.resizerX}`}></div>

      <div className={classes.flexRow} style={{ flex: '40%', whiteSpace: 'nowrap', minWidth: '300px' }}>

        <div style={{ width: 'fit-content', zIndex: 11, pointerEvents: jsonError ? 'none' : '', opacity: jsonError ? '0.3' : 1 }}>
          {jsonWithKeys ? 
            <TreeView data={jsonWithKeys} pasteValToJson={pasteValToJson} />
          : null}
        </div>

        <div className={classes.flex} style={{ width: '100%' }}>
          <div style={{ border: '1px solid #fff', width: '100%' }}>
            <div style={{ color: '#4c4c4c', backgroundColor: '#ffffff80' }} className={`${classes.alignCenter} ${classes.p5}`}>
              <span className={classes.mr20} style={{ fontSize: 10 }}>classification</span>
              <div
                className={`${classes.alignCenter} ${classes.mr20}`}
              >
                <div
                  className={`${classes.alignCenter}`}
                  style={{ width: '200px', opacity: 1 }}
                >
                  <span className={`${classes.mr10} ${classes.ml5}`}>Human:</span>
                  <TrplSelect
                    margin="none"
                    values={[
                      '',
                      ...finetune_classif
                    ]}
                    onChange={(e) => {
                      e.preventDefault();
                      setSendingClassifData(true);
                      sendClassification(message.id, e.target.value, () => {
                        setSendingClassifData(false);
                      });
                    }}
                    value={message.finetune_classif}
                    formControlClassName={classes.filterInput}
                    style={{
                      color: `${message.finetune_classif
                        ? classifColors[finetune_classif.indexOf(message.finetune_classif)] : '#4C4C4C'}`,
                      fontWeight: 600,
                    }}
                  />
                </div>
                {sendingClassifData ? <LoaderNew className={classes.ml10}/> : null}
              </div>
            
              {message.gpt_finetune_classif ?
                <div
                  className={`${classes.alignCenter}`}
                  style={{
                    minHeight: '24px',
                    pointerEvents: message.gpt_finetune_classif ? 'none' : '',
                  }}
                >
                  <span className={`${classes.mr10} ${classes.ml10}`}>GPT:</span>
                  <span
                    style={{
                      fontWeight: 600,
                      marginBottom: '2px',
                      color: classifColors[finetune_classif.indexOf(message.gpt_finetune_classif)]
                    }}
                  >
                    {message.gpt_finetune_classif}
                  </span>
                </div>
              : null}
            </div>

            <div className={classes.spaceBetween}>
              <div className={`${classes.alignCenter}`} style={{ padding: '10px 5px 5px 5px' }}>
                <Tabs
                  className={classes.smallTabs}
                  style={{ justifyContent: 'flex-start' }}
                  value={currentJsonTabValue}
                  onChange={(e, value) => {
                    setCurrentJsonTabValue(value);
                  }}
                >
                  <Tab
                    value={'human'}
                    style={{
                      color: message.finetune_compl ? '#4c4c4c' : '#BAB9DB',
                      border: (currentJsonTabValue === 'human' && message.finetune_compl) ? '1px solid #4c4c4c' : '#BAB9DB',
                      backgroundColor: currentJsonTabValue === 'human' ? '#FFD4B7' : ''
                    }}
                    // label={countedTokens && countedTokens.compl_tokens ?
                    //   `*${countedTokens.compl_tokens + '* human'}` : 'human'}
                    label={complicatedTabLabel('human', 'compl_tokens')}
                  />
                  {message.parsings && message.parsings.map((parsing, index) => {
                    const modelKey = parsing.model;
                    const modelTitle = `${'[' + modelKey + '] ' + llm_models[modelKey]}`;
                    const messageGptParsing = parsing.result;
                    return (
                      <Tab
                        key={index}
                        value={index}
                        title={modelTitle}
                        style={{
                          color: messageGptParsing ? '#4c4c4c' : '#BAB9DB',
                          border: (messageGptParsing && (currentJsonTabValue === index)) ? '1px solid #4c4c4c' : '#BAB9DB',
                          backgroundColor: currentJsonTabValue === index ? llmModelsColors[modelKey] : '',
                        }}
                        label={modelTitle}
                      />
                    )
                  })}
                </Tabs>
              </div>
            </div>

            <div className={`${classes.alignCenter}`} style={{ height: '15px' }}>
              {currentGptSelectedParsingData ?
                <div className={`${classes.pl10} ${classes.flex}`} style={{ gap: '5px', fontSize: 10, color: '4c4c4c' }}>
                  <span>prompt_tokens: {currentGptSelectedParsingData.prompt_tokens}</span>
                  <span>result_tokens: {currentGptSelectedParsingData.result_tokens}</span>
                </div>
              : 
                <div>
                  {jsonError ? <span style={{ color: 'red' }}>{jsonError}</span> : null}
                </div>}
            </div>

            {currentJsonTabValue !== 'human' && currentGptSelectedParsingData ?
              <div
                style={{
                  width: '100%',
                  height: 'calc(100% - 80px)',
                }}
              >
                <Macros
                  fullWidth={true}
                  nonResizable={true}
                  searchValue={searchValue}
                  borderColor={currentGptSelectedParsingData.is_valid ? '#fff' : '#D91920'}
                  linesNumberToPx={
                    gptJson && gptJson.includes("\n") && gptJson.split("\n").length < 50
                    ? (16 * gptJson.split("\n").length + 16) + 'px'
                      : gptJson && gptJson.includes("\n") && gptJson.split("\n").length > 50 ? '800px' : '90px'}
                  editable={false}
                  showCode={true}
                  functionVal={gptJson}
                />
              </div>
              :
              <React.Fragment>
                <div
                  className={`${classes.alignCenter} ${classes.pr10}`}
                  style={{
                    borderLeft: `5px solid ${jsonSaved ? 'limegreen' : '#fff'}`,
                    backgroundColor: '#fff',
                    width: '100%',
                    height: 'calc(100% - 80px)',
                  }}
                >
                  <Macros
                    fullWidth={true}
                    nonResizable={true}
                    borderColor={highlightAsNotValid || jsonError ? '#D91920' : '#fff'}
                    oldMacro={initialJson || finetune_template}
                    linesNumberToPx={
                      json && json.includes("\n") && json.split("\n").length < 50
                      ? (16 * json.split("\n").length + 16) + 'px'
                        : json && json.includes("\n") && json.split("\n").length > 50 ? '800px' : '90px'}
                    searchValue={searchValue}
                    editable={true}
                    showCode={true}
                    functionVal={json}
                    setFunctionPerfValues={(type, val) => {
                      setJson(val);
                    }}
                  />
                  <div className={classes.mr10}>
                    <button
                      size="small"
                      className={`${classes.btn} ${classes.btnBigNormal}`}
                      style={{
                        minWidth: '40px',
                        textTransform: 'capitalize',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setSendingMacroData(true);
                        onSaveJsonToDB(message.id, json, index, () => {
                          setSendingMacroData(false);
                          if (json) {
                            setInitialJson(json);
                            setJsonSaved(true);
                          } else {
                            setInitialJson(template);
                            setJson(template);
                            setJsonSaved(false);
                          }
                        });
                      }}
                    >
                      {sendingMacroData ? <LoaderNew /> : 'Set'}
                    </button>
                  </div>
                  {/* <pre style={{ fontSize: '5px', color: 'c4c4c4' }}>{json}</pre> */}
                </div>
              </React.Fragment>
            }
          </div>
        </div>
      </div>
    </div>
  );
});

// {
//   pickup: [
//     {
//       zip: 60626,
//       city: "Chicago",
//       state: "Il",
//       address: '6930 N Greenview',
//       when: [
//         {
//           date: "today",
//           time: "5pm",
//           timezone: null,
//           appointment: null,
//           to: {
//             date: null,
//             time: null,
//             timezone: null,
//           }
//         },
//         {
//           date: null,
//           time: null,
//           timezone: null,
//           appointment: null,
//           to: {
//             date: null,
//             time: null,
//             timezone: null,
//           }
//         },
//       ],
//       total_weight: null,
//       items: [
//         {
//           pieces: null,
//           type: null,
//           weight_each: null,
//         }
//       ],
//       driver_assist: null,
//       dock_level: null,
//       liftgate: null,
//     },
//     {
//       zip: 60118,
//       city: null,
//       state: null,
//       address: null,
//       when: [
//         {
//           date: null,
//           time: null,
//           timezone: null,
//           appointment: null,
//           to: {
//             date: null,
//             time: null,
//             timezone: null,
//           }
//         }
//       ],
//       total_weight: null,
//       items: [
//         {
//           pieces: null,
//           type: null,
//           weight_each: null,
//         }
//       ],
//       driver_assist: null,
//       dock_level: null,
//       liftgate: null,
//     }
//   ],
//   delivery: [
//     {
//       zip: null,
//       city: null,
//       state: null,
//       address: null,
//       when: [
//         {
//           date: null,
//           time: null,
//           timezone: null,
//           appointment: true,
//           to: {
//             date: null,
//             time: null,
//             timezone: null,
//           }
//         },
//       ],
//       driver_assist: null,
//       dock_level: null,
//       liftgate: null,
//       white_glove: null,
//     }
//   ],
//   suggested_truck_size: null,
//   team: null,
//   stackable: null,
//   hazardous: null,
//   notes: null,
// }

// {
//   pickup: [
//     {
//       zip: 'pickup/0/zip',
//       city: 'pickup/0/city',
//       state: 'pickup/0/state',
//       address: 'pickup/0/address',
//       when: [
//         {
//           date: 'pickup/0/when/0/date',
//           time: 'pickup/0/when/0/time',
//           timezone: 'pickup/0/when/0/timezone',
//           appointment: 'pickup/0/when/0/appointment',
//           to: {
//             date: 'pickup/0/when/0/to/date',
//             time: 'pickup/0/when/0/to/time',
//             timezone: 'pickup/0/when/0/to/timezone'
//           }
//         },
//         {
//           date: 'pickup/0/when/1/date',
//           time: 'pickup/0/when/1/time',
//           timezone: 'pickup/0/when/1/timezone',
//           appointment: 'pickup/0/when/1/appointment',
//           to: {
//             date: 'pickup/0/when/0/to/date',
//             time: 'pickup/0/when/0/to/time',
//             timezone: 'pickup/0/when/0/to/timezone'
//           }
//         },
//       ],
//       total_weight: 'pickup/0/total_weight',
//       items: [
//         {
//           pieces: 'pickup/0/items',
//           type: 'pickup/0/type',
//           weight_each: 'pickup/0/weight_each',
//         }
//       ],
//       driver_assist: 'pickup/0/driver_asist',
//       dock_level: 'pickup/0/dock_level',
//       liftgate: 'pickup/0/liftgate',
//     },
//     {
//       zip: 'pickup/1/zip',
//       city: 'pickup/1/city',
//       state: 'pickup/1/state',
//       address: 'pickup/1/address',
//       when: [
//         {
//           date: 'pickup/1/when/0/date',
//           time: 'pickup/1/when/0/time',
//           timezone: 'pickup/1/when/0/timezone',
//           appointment: 'pickup/1/when/0/appointment',
//           to: {
//             date: 'pickup/1/when/0/to/date',
//             time: 'pickup/1/when/0/to/time',
//             timezone: 'pickup/1/when/0/to/timezone'
//           }
//         }
//       ],
//       total_weight: 'pickup/1/total_weight',
//       items: [
//         {
//           pieces: 'pickup/1/items',
//           type: 'pickup/1/type',
//           weight_each: 'pickup/1/weight_each',
//         }
//       ],
//       driver_assist: 'pickup/1/driver_asist',
//       dock_level: 'pickup/1/dock_level',
//       liftgate: 'pickup/1/liftgate',
//     }
//   ],
//   delivery: [
//     {
//       zip: 'delivery/0/zip',
//       city: 'delivery/0/city',
//       state: 'delivery/0/state',
//       address: 'delivery/0/address',
//       when: [
//         {
//           date: 'delivery/0/when/0/date',
//           time: 'delivery/0/when/0/time',
//           timezone: 'delivery/0/when/0/timezone',
//           appointment: 'delivery/0/when/0/appointment',
//           to: {
//             date: 'delivery/0/when/0/to/date',
//             time: 'delivery/0/when/0/to/time',
//             timezone: 'delivery/0/when/0/to/timezone'
//           }
//         },
//       ],
//       driver_assist: 'delivery/0/driver_asist',
//       dock_level: 'delivery/0/dock_level',
//       liftgate: 'delivery/0/liftgate',
//       white_glove: 'white_glove/0/liftgate'
//     }
//   ],
//   suggested_truck_size: 'suggested_truck_size',
//   team: 'team',
//   stackable: 'stackable',
//   hazardous: 'hazardous',
//   notes: 'notes'
// }
